<template>
  <div class="col-md-12">
    <div class="verify-container">
      <div class="verify-icon">
        <router-link :to="{ name: 'public-homepage'}" class="header-logo">
          <img src="@/assets/img/logo.png">
        </router-link>
      </div>

      <!-- État de chargement -->
      <div v-if="loading" class="verify-loading">
        <div class="spinner-border spinner-border-lg" role="status">
          <span class="sr-only">Vérification en cours...</span>
        </div>
        <p class="verify-loading-text">Vérification de votre email en cours...</p>
      </div>

      <!-- Succès -->
      <div v-if="!loading && success" class="verify-content">
        <div class="verify-title">Email vérifié avec succès !</div>

        <div class="verify-text">
          <p class="verify-text-main">{{ message }}</p>
          <p class="verify-text-important">
            <strong>Vous pouvez maintenant vous connecter et accéder à votre espace candidat.</strong>
          </p>
        </div>

        <div class="verify-action">
          <router-link :to="{ name: 'public-login' }" class="btn-verify-primary">
            Se connecter
          </router-link>
        </div>
      </div>

      <!-- Erreur -->
      <div v-if="!loading && !success && error" class="verify-content">
        <div class="verify-title verify-title-error">Erreur de vérification</div>

        <div class="verify-text">
          <p class="verify-text-error">{{ errorMessage }}</p>
        </div>

        <!-- Renvoi de l'email -->
        <div v-if="canResendEmail" class="verify-resend-box">
          <div class="verify-resend-content">
            <p class="verify-resend-title">Renvoyer l'email de vérification</p>
            <p class="verify-resend-text">Le lien de vérification a expiré ou est invalide.</p>

            <div class="verify-resend-form">
              <input
                  v-model="email"
                  class="verify-input"
                  placeholder="Votre adresse email"
                  type="email"
              >
              <button
                  :disabled="resendLoading || !email"
                  class="btn-verify-secondary"
                  @click="resendVerificationEmail"
              >
                <span v-show="resendLoading" class="spinner-border spinner-border-sm mr-2"></span>
                Renvoyer l'email
              </button>
            </div>
          </div>
        </div>

        <!-- Succès du renvoi -->
        <div v-if="resendSuccess" class="verify-success-box">
          <div class="verify-success-icon">✓</div>
          <div class="verify-success-content">
            <p class="verify-success-text">
              Email de vérification renvoyé avec succès ! Veuillez vérifier votre boîte mail.
            </p>
          </div>
        </div>

        <div class="verify-action">
          <router-link :to="{ name: 'public-login' }" class="btn-verify-link">
            Retour à la connexion
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthApi from "@/api/AuthApi";
import RouteEnum from "@/router/RouteEnum";

export default {
  name: "VerifyEmail",
  data() {
    return {
      loading: true,
      success: false,
      error: false,
      message: "",
      errorMessage: "",
      canResendEmail: false,
      email: "",
      resendLoading: false,
      resendSuccess: false,
    };
  },
  mounted() {
    const token = this.$route.query.token;

    if (!token) {
      this.loading = false;
      this.error = true;
      this.errorMessage = "Token de vérification manquant.";
      return;
    }

    this.verifyEmail(token);
  },
  methods: {
    verifyEmail(token) {
      this.loading = true;
      AuthApi.verifyEmail(token)
          .then((response) => {
            this.loading = false;
            this.success = true;
            this.message = response.data.message || "Votre email a été vérifié avec succès. Vous pouvez maintenant vous connecter.";

            // Redirection vers la page de connexion après 5 secondes
            setTimeout(() => {
              this.$router.push({name: RouteEnum.LOGIN});
            }, 5000);
          })
          .catch((error) => {
            this.loading = false;
            this.error = true;

            if (error.response && error.response.data) {
              const errorCode = error.response.data.code;
              this.errorMessage = error.response.data.message || "Une erreur est survenue lors de la vérification.";

              // Permettre le renvoi si le token est expiré ou invalide
              if (errorCode === 'token_expired' || errorCode === 'invalid_token') {
                this.canResendEmail = true;
              } else if (errorCode === 'already_verified') {
                this.errorMessage = "Cet email a déjà été vérifié. Vous pouvez vous connecter.";
              }
            } else {
              this.errorMessage = error.message || "Une erreur est survenue lors de la vérification.";
            }
          });
    },

    resendVerificationEmail() {
      if (!this.email) {
        return;
      }

      this.resendLoading = true;
      this.resendSuccess = false;

      AuthApi.resendVerificationEmail(this.email)
          .then((response) => {
            this.resendLoading = false;
            this.resendSuccess = true;
            this.canResendEmail = false;
          })
          .catch((error) => {
            this.resendLoading = false;
            const errorMsg = (error.response && error.response.data && error.response.data.message)
                || "Une erreur est survenue lors de l'envoi de l'email.";
            alert(errorMsg);
          });
    }
  }
};
</script>

<style scoped>
.verify-container {
  max-width: 600px;
  margin: 40px auto;
  padding: 40px 30px;
  text-align: center;
  background: #ffffff;
  border-radius: 12px;
}

.verify-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.verify-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 0;
}

.verify-loading-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.verify-content {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.verify-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 24px;
  line-height: 1.3;
}

.verify-title-error {
  color: #dc2626;
}

.verify-text {
  margin-bottom: 32px;
}

.verify-text p {
  margin-bottom: 16px;
  line-height: 1.6;
}

.verify-text-main {
  font-size: 16px;
  color: #4b5563;
}

.verify-text-important {
  font-size: 16px;
  color: #1f2937;
  background: rgba(203, 219, 42, 0.18);
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #CBDB2A;
  margin-bottom: 0;
}

.verify-text-important strong {
  font-weight: 600;
}

.verify-text-error {
  font-size: 16px;
  color: #dc2626;
  background: rgba(220, 38, 38, 0.08);
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #dc2626;
}

.verify-resend-box {
  margin-bottom: 24px;
  padding: 24px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.verify-resend-title {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  font-size: 16px;
}

.verify-resend-text {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 16px;
  line-height: 1.5;
}

.verify-resend-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.verify-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  transition: border-color 0.2s;
}

.verify-input:focus {
  outline: none;
  border-color: #CBDB2A;
  box-shadow: 0 0 0 3px rgba(203, 219, 42, 0.1);
}

.verify-success-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(16, 185, 129, 0.08);
  border-radius: 8px;
  border-left: 4px solid #10b981;
  text-align: left;
  margin-bottom: 24px;
}

.verify-success-icon {
  font-size: 24px;
  color: #10b981;
  font-weight: bold;
  flex-shrink: 0;
}

.verify-success-content {
  flex: 1;
}

.verify-success-text {
  color: #047857;
  font-size: 15px;
  margin: 0;
  line-height: 1.5;
}

.verify-action {
  margin-top: 24px;
}

.btn-verify-primary {
  display: inline-block;
  padding: 12px 32px;
  background: #CBDB2A;
  color: #1f2937;
  font-weight: 600;
  font-size: 16px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s;
}

.btn-verify-primary:hover {
  background: #b8c625;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(203, 219, 42, 0.3);
  color: #1f2937;
  text-decoration: none;
}

.btn-verify-secondary {
  width: 100%;
  padding: 12px 24px;
  background: #2563eb;
  color: white;
  font-weight: 600;
  font-size: 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-verify-secondary:hover:not(:disabled) {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-verify-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-verify-link {
  display: inline-block;
  padding: 8px 16px;
  color: #2563eb;
  font-weight: 500;
  font-size: 15px;
  text-decoration: none;
  transition: color 0.2s;
}

.btn-verify-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .verify-container {
    margin: 20px;
    padding: 30px 20px;
  }

  .verify-title {
    font-size: 24px;
  }

  .verify-text-main,
  .verify-text-important,
  .verify-text-error {
    font-size: 15px;
  }

  .verify-resend-box {
    padding: 20px;
  }
}
</style>
